import { supabase, safeSupabaseQuery } from './supabase';
import type { Parcours, ParcoursIndexItem, ParcoursStep } from '../types/parcours';

const PARCOURS_INDEX_COLS = [
  'id', 'slug', 'company_name', 'company_logo', 'company_cover',
  'tagline', 'description', 'sector', 'location', 'employee_count',
  'featured', 'status', 'meta_title', 'meta_description', 'og_image',
  'published_at', 'created_at', 'updated_at',
].join(', ');

export async function fetchParcoursIndex(): Promise<ParcoursIndexItem[]> {
  try {
    const { data, error } = await safeSupabaseQuery(() =>
      supabase
        .from('parcours')
        .select(PARCOURS_INDEX_COLS)
        .eq('status', 'published')
        .order('published_at', { ascending: false })
    );

    if (error || !data) return [];

    // Sort featured items first (client-side to avoid PostgREST multi-order quirks)
    const sorted = [...(data as ParcoursIndexItem[])].sort((a, b) =>
      Number(b.featured) - Number(a.featured)
    );
    return sorted;
  } catch {
    return [];
  }
}

// Metadata only — public, no steps. Used for teaser landing.
export async function fetchParcoursTeaserBySlug(slug: string): Promise<Parcours | null> {
  try {
    const { data, error } = await safeSupabaseQuery(() =>
      supabase
        .from('parcours')
        .select('*')
        .eq('slug', slug)
        .eq('status', 'published')
        .maybeSingle()
    );

    if (error || !data) return null;
    return { ...data, steps: [] } as Parcours;
  } catch {
    return null;
  }
}

// Full parcours + steps — requires authenticated session.
// If steps fail (RLS), returns parcours with empty steps rather than throwing.
export async function fetchParcoursBySlug(slug: string): Promise<Parcours | null> {
  try {
    const { data: parcours, error } = await safeSupabaseQuery(() =>
      supabase
        .from('parcours')
        .select('*')
        .eq('slug', slug)
        .eq('status', 'published')
        .maybeSingle()
    );

    if (error || !parcours) return null;

    const { data: steps } = await safeSupabaseQuery(() =>
      supabase
        .from('parcours_steps')
        .select('*')
        .eq('parcours_id', parcours.id)
        .eq('status', 'published')
        .order('order_index', { ascending: true })
    );

    // NB : le module "À vous de jouer" (formulaire rencontrer l'équipe)
    // n'est PAS injecté automatiquement — certains parcours sont des
    // onboardings post-signature. Il s'ajoute manuellement depuis
    // l'éditeur d'étapes admin (bloc 'a-vous-de-jouer').
    return { ...parcours, steps: (steps ?? []) as ParcoursStep[] } as Parcours;
  } catch {
    return null;
  }
}

export async function fetchParcoursStep(
  companySlug: string,
  stepId: string
): Promise<{ parcours: Parcours; step: ParcoursStep } | null> {
  try {
    const parcours = await fetchParcoursBySlug(companySlug);
    if (!parcours) return null;

    const steps = parcours.steps ?? [];
    const step = steps.find((s) => s.step_id === stepId) ?? null;
    if (!step) return null;

    return { parcours, step };
  } catch {
    return null;
  }
}
